import { useEffect, useState } from "react";
import { TrendingUp, Waves, Percent, Scale } from "lucide-react";
import { StatCard } from "../ui/StatCard";
import { Button } from "../ui/Button";
import { eur } from "../../lib/format";
import { MONTHS_NL } from "../../lib/format";
import api from "../../lib/api";
import type { ProfitLossResult, CashflowResult, VatSummary, BalanceResult } from "../../types/bank";

type Sub = "pl" | "cash" | "vat" | "balance";

const subLabels: { key: Sub; label: string; icon: typeof TrendingUp }[] = [
  { key: "pl", label: "Winst & verlies", icon: TrendingUp },
  { key: "cash", label: "Cashflow", icon: Waves },
  { key: "vat", label: "BTW", icon: Percent },
  { key: "balance", label: "Balans", icon: Scale },
];

/** Rapporten: W&V, cashflow, BTW en balans met periode-selector. */
export function ReportsPanel() {
  const now = new Date();
  const [year, setYear] = useState(now.getUTCFullYear());
  const [sub, setSub] = useState<Sub>("pl");
  const [pl, setPl] = useState<ProfitLossResult | null>(null);
  const [cash, setCash] = useState<CashflowResult | null>(null);
  const [vat, setVat] = useState<VatSummary | null>(null);
  const [bal, setBal] = useState<BalanceResult | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get("/api/reports/profit-loss", { params: { year } }).then((r) => setPl(r.data)),
      api.get("/api/reports/cashflow", { params: { year } }).then((r) => setCash(r.data)),
      api.get("/api/reports/vat", { params: { year } }).then((r) => setVat(r.data)),
      api.get("/api/reports/balance", { params: { year } }).then((r) => setBal(r.data)),
    ])
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [year]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          {subLabels.map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setSub(key)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                sub === key ? "bg-white text-slate-800 shadow" : "text-slate-500 hover:text-slate-700"
              }`}
            >
              <Icon size={16} /> {label}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" onClick={() => setYear((y) => y - 1)}>←</Button>
          <span className="font-semibold text-slate-700">{year}</span>
          <Button variant="ghost" size="sm" onClick={() => setYear((y) => y + 1)}>→</Button>
        </div>
      </div>

      {loading && <p className="text-sm text-slate-400">Rapporten laden…</p>}

      {sub === "pl" && pl && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard label="Omzet (excl. btw)" value={eur(pl?.totals.omzetExcl ?? 0)} icon={TrendingUp} color="brand" />
            <StatCard label="Kosten (excl. btw)" value={eur(pl?.totals.kostenExcl ?? 0)} icon={TrendingUp} color="rose" />
            <StatCard label={winstLabel(pl?.totals.winst ?? 0)} value={eur(pl?.totals.winst ?? 0)} icon={TrendingUp} color={(pl?.totals.winst ?? 0) >= 0 ? "emerald" : "rose"} />
          </div>
          <div className="surface-card p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                  <th className="py-2 pr-3">Maand</th>
                  <th className="py-2 pr-3 text-right">Omzet</th>
                  <th className="py-2 pr-3 text-right">Kosten</th>
                  <th className="py-2 text-right">Resultaat</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pl?.months.map((m) => (
                  <tr key={m.month}>
                    <td className="py-2 pr-3 text-slate-700">{MONTHS_NL[m.month - 1]}</td>
                    <td className="py-2 pr-3 text-right">{eur(m.omzetExcl)}</td>
                    <td className="py-2 pr-3 text-right">{eur(m.kostenExcl)}</td>
                    <td className={`py-2 text-right font-semibold ${m.winst >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{eur(m.winst)}</td>
                  </tr>
                ))}
                {pl && (
                  <tr className="font-bold bg-slate-50">
                    <td className="py-2 pr-3">Totaal {year}</td>
                    <td className="py-2 pr-3 text-right">{eur(pl.totals.omzetExcl)}</td>
                    <td className="py-2 pr-3 text-right">{eur(pl.totals.kostenExcl)}</td>
                    <td className={`py-2 text-right ${pl.totals.winst >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{eur(pl.totals.winst)}</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </>
      )}

      {sub === "cash" && cash && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard label="Inkomend" value={eur(cash.totals.inEur)} icon={Waves} color="emerald" />
            <StatCard label="Uitgaand" value={eur(cash.totals.uitEur)} icon={Waves} color="rose" />
            <StatCard label="Netto" value={eur(cash.totals.netto)} icon={Waves} color={cash.totals.netto >= 0 ? "brand" : "rose"} />
          </div>
          <div className="surface-card p-6 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
                  <th className="py-2 pr-3">Maand</th>
                  <th className="py-2 pr-3 text-right">In</th>
                  <th className="py-2 pr-3 text-right">Uit</th>
                  <th className="py-2 pr-3 text-right">Netto</th>
                  <th className="py-2 pr-3 text-right">Niet geboekt in</th>
                  <th className="py-2 pr-3 text-right">Niet geboekt uit</th>
                  <th className="py-2 text-right">Eindsaldo</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {cash.months.map((m) => (
                  <tr key={m.month}>
                    <td className="py-2 pr-3 text-slate-700">{MONTHS_NL[m.month - 1]}</td>
                    <td className="py-2 pr-3 text-right text-emerald-600">{eur(m.inEur)}</td>
                    <td className="py-2 pr-3 text-right text-rose-600">{eur(m.uitEur)}</td>
                    <td className="py-2 pr-3 text-right font-semibold">{eur(m.netto)}</td>
                    <td className="py-2 pr-3 text-right text-slate-400">{m.nietGeboektIn > 0 ? eur(m.nietGeboektIn) : "—"}</td>
                    <td className="py-2 pr-3 text-right text-slate-400">{m.nietGeboektUit > 0 ? eur(m.nietGeboektUit) : "—"}</td>
                    <td className="py-2 text-right">{m.eindsaldo != null ? eur(m.eindsaldo) : "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="text-xs text-slate-400 mt-3">"Niet geboekt" = betalingen/uitgaven die nog niet via een bankimport zijn gekoppeld.</p>
          </div>
        </>
      )}

      {sub === "vat" && vat && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <StatCard label="BTW omzet" value={eur(vat.omzetBtw)} icon={Percent} color="brand" />
            <StatCard label="BTW aankopen" value={eur(vat.aankoopBtw)} icon={Percent} color="indigo" />
            <StatCard label="BTW te betalen" value={eur(vat.teBetalen)} icon={Percent} color={vat.teBetalen >= 0 ? "amber" : "emerald"} />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <VatTable title="Omzet" rows={vat.sales} />
            <VatTable title="Aankopen" rows={vat.purchases} />
          </div>
        </>
      )}

      {sub === "balance" && bal && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard label="Bank-saldo" value={bal.bankSaldo != null ? eur(bal.bankSaldo) : "—"} icon={Scale} color="brand" />
            <StatCard label="Open debiteuren" value={eur(bal.totalOpenDebiteuren)} icon={Scale} color="emerald" />
            <StatCard label="Open crediteuren" value={eur(bal.totalOpenCrediteuren)} icon={Scale} color="rose" />
            <StatCard label="BTW-saldo" value={eur(bal.btwSaldo)} icon={Scale} color="amber" />
          </div>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="surface-card p-6">
              <h3 className="font-bold text-slate-800 mb-3">Openstaande debiteuren ({bal.debiteuren.length})</h3>
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {bal.debiteuren.map((d) => (
                  <div key={d.id} className="py-2 flex items-center justify-between text-sm">
                    <span className="text-slate-700 truncate">{d.number} · {d.contactName ?? "—"}</span>
                    <span className="font-semibold text-emerald-600 shrink-0 ml-3">{eur(d.openEur)}</span>
                  </div>
                ))}
                {bal.debiteuren.length === 0 && <p className="text-sm text-slate-400 py-2">Geen openstaande facturen.</p>}
              </div>
            </div>
            <div className="surface-card p-6">
              <h3 className="font-bold text-slate-800 mb-3">Openstaande crediteuren ({bal.crediteuren.length})</h3>
              <div className="divide-y divide-slate-100 max-h-96 overflow-y-auto">
                {bal.crediteuren.map((c) => (
                  <div key={c.id} className="py-2 flex items-center justify-between text-sm">
                    <span className="text-slate-700 truncate">{c.description}{c.merchant ? ` · ${c.merchant}` : ""}</span>
                    <span className="font-semibold text-rose-600 shrink-0 ml-3">{eur(c.amountEur)}</span>
                  </div>
                ))}
                {bal.crediteuren.length === 0 && <p className="text-sm text-slate-400 py-2">Geen openstaande uitgaven.</p>}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function winstLabel(w: number): string {
  return w >= 0 ? "Winst" : "Verlies";
}

function VatTable({ title, rows }: { title: string; rows: { percentage: number; basis: number; btw: number }[] }) {
  return (
    <div className="surface-card p-6">
      <h3 className="font-bold text-slate-800 mb-3">BTW {title.toLowerCase()}</h3>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs text-slate-400 uppercase tracking-wider">
            <th className="py-2 pr-3">Tarief</th>
            <th className="py-2 pr-3 text-right">Basis</th>
            <th className="py-2 text-right">BTW</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {rows.map((r) => (
            <tr key={r.percentage}>
              <td className="py-2 pr-3">{r.percentage}%</td>
              <td className="py-2 pr-3 text-right">{eur(r.basis)}</td>
              <td className="py-2 text-right font-semibold">{eur(r.btw)}</td>
            </tr>
          ))}
          {rows.length === 0 && <tr><td colSpan={3} className="py-3 text-center text-slate-400">Geen posten.</td></tr>}
        </tbody>
      </table>
    </div>
  );
}